
import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import vSelect from "vue-select"

import VuePerfectScrollbar from "vue-perfect-scrollbar"

import ParameterValue from "../Controls/ParameterValue.vue"
import { Field } from "@/types/field"
import { CaseColumn } from "@/types/caseColumns"
import { LabeledValue } from "@/types/scheduleSettings"

interface SelectedField {
	fieldData: any;
	dataType?: string;
	value?: any;
	dynamicValue?: boolean
}

@Component({
	components: {
		"v-select": vSelect,
		"parameter-value": ParameterValue,
		"scrollbar": VuePerfectScrollbar
	}
})
export default class FieldAndValue extends Vue {
	/**
	 * The list of all fields and their values
	 * */
	@Prop({ type: Object, default: () => { } })
	value!: Record<string, LabeledValue>;

	/**
	 * A list of all Fields that can be added
	 */
	@Prop({ type: Array, required: true })
	fields!: CaseColumn[];

	/**
	 * If an empty value can be set for fields
	 */
	@Prop({ type: Boolean, default: true })
	allowEmptyFieldValue!: boolean;

	selectedFields: SelectedField[] = []

	private columns =
		[
			{
				key: "field",
				label: "Field"
			},
			{
				key: "value",
				label: "Value"
			},
			{
				key: "buttons",
				label: ""
			}
		]

	get isValid (): boolean {
		if (!this.selectedFields) {
			return true
		}

		const selectedFieldType = this.selectedFields
			.map(x => x.fieldData)
			.filter(x => x && x.dataType !== "list-multiple")

		return selectedFieldType.length === [...new Set(selectedFieldType)].length
	}

	private async mounted () {
		this.onValueChange()
	}

	/* Handler for when a field is selected in dropdown */
	fieldSelected (selectedField: SelectedField) {
		selectedField.value = undefined
		selectedField.dynamicValue = false

		this.triggerInput()
	}

	/** Emits an input event */
	triggerInput () {
		let newFields:Record<string, LabeledValue> = this.selectedFields
			.filter(x => !!x.fieldData)
			.reduce((a, x) => (
				{
					...a,
					[x.fieldData.key]: { label: x.fieldData.key, value: x.value }
				}
			), {})

		if (this.selectedFields.every(f => !!f.value) /* all fields have value */ ||
				(this.allowEmptyFieldValue &&
					this.selectedFields.every(f => f.fieldData && !!f.fieldData.key) /* all fields have key */)
		) {
			this.$emit("input", newFields)
		}
	}

	/**
	 * On value prop being changed, remove any corresponding selected fields
	 * @private
	 */
	@Watch("value")
	private onValueChange () {
		this.selectedFields = Object.keys(this.value).map((fieldKey: string) => {
			return {
				fieldData: this.fields.find(f => f.key === fieldKey),
				value: this.value[fieldKey].value,
				dynamicValue: this.value[fieldKey].dynamicValue === undefined ? false : this.value[fieldKey].dynamicValue
			}
		})
	}

	/**
	 * On Fields prop being changed, remove any corresponding selected fields
	 * @private
	 */
	@Watch("fields")
	private onFieldsChange () {
		if (!this.selectedFields) return

		const filteredOutFields = this.selectedFields
			.filter(y => y.fieldData !== undefined) // filter out in progress fields
			.filter(x => !this.fields.map(x => x.key).includes(x.fieldData.key))

		filteredOutFields.forEach(field => this.removeField(field))

		// Refresh value
		this.onValueChange()
	}

	private dynamicValueChanged (field: SelectedField, newValue: boolean) {
		Vue.set(field, "dynamicValue", newValue)
		Vue.set(field, "value", null)
	}

	/**
	 * Adds a blank field to the list.
	 */
	public addField () {
		if (!this.selectedFields) {
			this.selectedFields = []
		}

		// If the last added field is not set, don't add another
		if (this.selectedFields.length > 0 &&
				!this.selectedFields[this.selectedFields.length - 1].fieldData) {
			return
		}

		this.selectedFields.push({
			fieldData: null,
			dynamicValue: false
		})
	}

	/**
	 * Removes a field from the list.
	 * @param field The field to remove
	 * @private
	 */
	private removeField (field: SelectedField) {
		this.selectedFields.splice(this.selectedFields.indexOf(field), 1)
		this.triggerInput()
	}
}
